import React from 'react'
import { useTheme } from '@/context/ThemeContext'

// A shaped skeleton rather than a centred spinner: the dashboard's layout is fixed,
// so showing where the brief, the cards and the list will land makes the load feel
// like the page arriving instead of a blank wait, and stops the content jumping.
//
// `activeBackground` (not `hoverBackground`) for the base — it has enough contrast
// against the page background to actually read as a block, and a sweeping
// highlight band on top of it stands in for "shine" since plain opacity pulsing
// was too subtle to notice at this contrast level.
const Block: React.FC<{ className?: string; radius?: string }> = ({ className, radius = '0.75rem' }) => {
  const { currentTheme: t } = useTheme()
  return (
    <div
      className={`relative overflow-hidden ${className ?? ''}`}
      style={{ backgroundColor: t.activeBackground, borderRadius: radius, opacity: 0.6 }}
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)',
          backgroundSize: '60% 100%',
          backgroundRepeat: 'no-repeat',
          animation: 'zephSkeletonShimmer 1.6s ease-in-out infinite',
        }}
      />
    </div>
  )
}

const DashboardSkeleton: React.FC = () => (
  <div className="flex flex-col gap-4" aria-busy="true" aria-label="Loading your dashboard">
    <Block className="h-44 w-full" radius="1rem" />
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      <Block className="h-24" />
      <Block className="h-24" />
      <Block className="h-24" />
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-4 items-start">
      <Block className="h-96" radius="1rem" />
      <div className="flex flex-col gap-4">
        <Block className="h-44" radius="1rem" />
        <Block className="h-56" radius="1rem" />
      </div>
    </div>
  </div>
)

export default DashboardSkeleton
